import { useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Label } from "@/components/ui/label.jsx";
import { Button } from "@/components/ui/button.jsx";
import { useToast } from "@/hooks/use-toast.js";
import { Upload, FileText, Loader2, Trash2, Eye } from "lucide-react";
import { useTaxClearances } from "@/lib/hooks/useCompanies.js";
import { api } from "@/lib/api/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog.jsx";

const TaxClearances = ({ companyId, onRefresh }) => {
  const { toast } = useToast();
  const [taxYear, setTaxYear] = useState("");
  const [taxFiles, setTaxFiles] = useState(null);
  const [isUploadingTax, setIsUploadingTax] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const taxInputRef = useRef(null);

  // Use the hook for data fetching and caching
  const {
    data: taxClearanceData,
    isLoading: isLoadingTaxClearance,
    refetch: refetchTaxClearances,
  } = useTaxClearances(companyId);

  // Generate year options for tax clearance (past years)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const onTaxFilesSelect = (e) => {
    const files = e.target.files;
    setTaxFiles(files);
  };

  const uploadTaxDocuments = async () => {
    if (!taxFiles || !companyId || !taxYear) return;

    setIsUploadingTax(true);

    // // Add all selected files to FormData
    // Array.from(taxFiles).forEach((file) => {
    //   formData.append("file", file);
    // });

    const response = await api.createTaxDeclaration(
      companyId,
      taxFiles[0],
      taxYear,
    );

    if (!response.ok) {
      toast({
        title: "Erreur de téléchargement",
        description:
          "Une erreur est survenue lors du téléchargement des documents fiscaux.",
        variant: "destructive",
      });
      setIsUploadingTax(false);
      return;
    }

    toast({
      title: "Documents fiscaux téléchargés",
      description: "Les documents fiscaux ont été téléchargés avec succès.",
    });

    // Reset form
    setTaxFiles(null);
    setTaxYear("");
    if (taxInputRef.current) {
      taxInputRef.current.value = "";
    }

    // Refresh local data
    refetchTaxClearances();

    // Notify parent if needed
    if (onRefresh) {
      onRefresh();
    }

    setIsUploadingTax(false);
  };

  const handleDeleteTaxClearance = async (declarationId) => {
    setIsDeleting(true);
    const response = await api.deleteTaxDeclaration(companyId, declarationId);

    if (!response.ok) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la liasse fiscale.",
        variant: "destructive",
      });
      setIsDeleting(false);
      return;
    }

    toast({ title: "Succès", description: "Liasse fiscale supprimée." });
    refetchTaxClearances();
    if (onRefresh) onRefresh();
    setIsDeleting(false);
  };

  // Render tax clearance documents
  const renderTaxClearanceDocuments = () => {
    if (isLoadingTaxClearance) {
      return (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Chargement des documents fiscaux...</span>
        </div>
      );
    }

    if (!taxClearanceData || taxClearanceData.length === 0) {
      return (
        <div className="flex items-center justify-center p-8 text-muted-foreground">
          <FileText className="h-6 w-6 mr-2" />
          <span>
            Les résultats des documents fiscaux seront affichés ici après
            traitement
          </span>
        </div>
      );
    }

    return (
<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {taxClearanceData.map((document) => {
          return (
            <div 
              key={document.id} 
              className="flex items-center gap-3 p-3 rounded-xl border bg-card hover:bg-accent/50 transition-all duration-200 shadow-sm"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold block truncate">
                  Liasse {document.year}
                </span>
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-8 w-8 p-0 bg-secondary hover:bg-secondary/80"
                  onClick={() => window.open(document.file, "_blank")}
                >
                  <Eye className="h-4 w-4" />
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-destructive hover:text-white hover:bg-destructive transition-colors"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer la liasse</AlertDialogTitle>
                      <AlertDialogDescription>
                        Êtes-vous sûr de vouloir supprimer la liasse fiscale {document.year} ? Cette action est irréversible.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteTaxClearance(document.id)}
                        className="bg-destructive hover:bg-destructive/90 text-white"
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {renderTaxClearanceDocuments()}

      {/* Tax clearance upload section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Télécharger la liasse fiscale
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="taxYear">Année fiscale</Label>
              <select
                id="taxYear"
                value={taxYear}
                onChange={(e) => setTaxYear(e.target.value)}
                className="w-full p-2 border border-input rounded-md bg-background"
              >
                <option value="">Sélectionner une année</option>
                {generateYearOptions().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <input
                ref={taxInputRef}
                type="file"
                multiple
                accept=".pdf,image/*"
                className="hidden"
                onChange={onTaxFilesSelect}
              />
              <Button
                type="button"
                className="h-10 flex-1"
                onClick={() => taxInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Sélectionner la liasse fiscale
              </Button>
              <Button
                type="button"
                onClick={uploadTaxDocuments}
                disabled={!taxFiles || !taxYear || isUploadingTax}
              >
                {isUploadingTax ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Téléchargement...
                  </>
                ) : (
                  "Télécharger"
                )}
              </Button>
            </div>
            {taxFiles && taxFiles.length > 0 && (
              <div className="p-3 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {taxFiles.length} fichier(s) sélectionné(s) pour {taxYear}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxClearances;